import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Field, change, touch } from 'redux-form';
import {
  getAvailableDocumentsByNationality,
  getDoc2BrandMap,
  getIgnoredDocuments,
} from '../utils/documentMaps';
import { selectRenderer } from '../utils/formRenderers';

/**
 * DocumentTypeField component.
 * Render a document type selector field that automatically updates based on passenger nationality.
 * Filters available document types according to nationality restrictions and brand configuration.
 *
 * @param {object} props - name, dataValueRef, target, passengerNationality, passengerDocumentType, documentIdValue, index, isDisabled, excludedDocumentBrands.
 * @param {string} props.name - The field name for the form.
 * @param {string} props.dataValueRef - Field name of the document ID input to mark as touched for validation.
 * @param {string} props.target - The target context (e.g., 'passengers', 'purchaser').
 * @param {string} props.passengerNationality - The passenger's nationality to filter available documents.
 * @param {string} props.passengerDocumentType - The currently selected document type.
 * @param {string} props.documentIdValue - Current value of the document ID field referenced by dataValueRef; used to skip touching empty fields on programmatic document-type changes.
 * @param {number} props.index - The passenger index in the form array.
 * @param {boolean} props.isDisabled - Whether the field should be disabled.
 * @param {Array<string>} props.excludedDocumentBrands - Brand strings to remove from the dropdown options.
 */
const DocumentTypeField = (props) => {
  const dispatch = useDispatch();
  const {
    name,
    target,
    passengerNationality,
    passengerDocumentType,
    documentIdValue,
    index,
    isDisabled,
    dataValueRef,
    excludedDocumentBrands,
  } = props;

  const { t } = useTranslation('seats');
  const ignoredDocuments = useMemo(() => getIgnoredDocuments(), []);
  const doc2BrandMap = useMemo(() => getDoc2BrandMap(target), [target]);
  const availableDocumentsByNationality = useMemo(() => getAvailableDocumentsByNationality(), []);
  const items = Object.entries(doc2BrandMap).filter(
    ([, value]) => !excludedDocumentBrands.includes(value),
  );

  const availableDocuments = useMemo(() => {
    return availableDocumentsByNationality[passengerNationality] || [];
  }, [availableDocumentsByNationality, passengerNationality]);

  const filteredItems = useMemo(() => {
    return availableDocuments.length > 0
      ? items.filter(([, value]) => availableDocuments.includes(value))
      : items;
  }, [items, availableDocuments]);

  // Get the document type from the passenger form if it exists in the available documents
  // otherwise, get the first available document type
  const documentType = useMemo(() => {
    return filteredItems.some(([, value]) => value === passengerDocumentType)
      ? passengerDocumentType
      : filteredItems[0]?.[1] || '';
  }, [filteredItems, passengerDocumentType]);

  const prevDocumentTypeRef = useRef(documentType);

  /**
   * Set the document type in the passenger form and touch the document id
   * field for validation. The touch is gated on the document id already
   * having a value so programmatic document-type changes on initial mount
   * (e.g., the FREE_FARE_DEFAULT_CPF auto-select for a non-autofilled
   * passenger) do not surface a false "required" error on an empty field
   * the user has not interacted with yet.
   */
  useEffect(() => {
    if (index === undefined) return;

    const documentTypeChanged = prevDocumentTypeRef.current !== documentType;

    if (documentTypeChanged) {
      dispatch(change('passengers', name, documentType));
      if (documentIdValue) dispatch(touch('passengers', dataValueRef));

      prevDocumentTypeRef.current = documentType;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType]);

  const options = useMemo(() => {
    return filteredItems
      .filter(
        ([key]) =>
          (name !== 'passengers[0].documentType' && name !== 'documentType') ||
          !ignoredDocuments.includes(key),
      )
      .map(([key, value]) => ({
        label: t('identification', { context: key }),
        value,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, ignoredDocuments, name]);

  return (
    <Field
      label={t('document_type')}
      placeholder={t('document_type')}
      component={selectRenderer}
      options={options}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      isDisabled={isDisabled}
    />
  );
};

DocumentTypeField.propTypes = {
  name: PropTypes.string.isRequired,
  dataValueRef: PropTypes.string.isRequired,
  target: PropTypes.string,
  passengerNationality: PropTypes.string,
  passengerDocumentType: PropTypes.string,
  documentIdValue: PropTypes.string,
  index: PropTypes.number,
  isDisabled: PropTypes.bool,
  excludedDocumentBrands: PropTypes.arrayOf(PropTypes.string),
};

DocumentTypeField.defaultProps = {
  documentIdValue: '',
  excludedDocumentBrands: [],
};

export default DocumentTypeField;
